import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { plotOfficerService } from '../../services/plotOfficerService';
import {
  FileText,
  MapPin,
  Calendar,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Save,
  Users,
  Sprout,
  Eye as EyeIcon,
  BarChart3,
  TrendingUp,
  Activity,
  Clock,
  CheckCircle,
  AlertCircle,
  Play
} from 'lucide-react';

const PlotOfficerDashboard = () => {
  const { user } = useAuth();
  const [assignedTrials, setAssignedTrials] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    trials: { total: 0, active: 0, completed: 0, pending: 0 },
    locations: { total: 0, active: 0, pending: 0, completed: 0 },
    dataEntries: { total: 0, thisWeek: 0, overdue: 0 }
  });
  const [recentActivities, setRecentActivities] = useState([]);

  // Load assigned trials from API with fallback to localStorage
  const loadAssignedTrials = async () => {
    try {
      const response = await plotOfficerService.getAssignedTrials();
      if (response.success) {
        setAssignedTrials(response.data || response);
      } else {
        throw new Error(response.message || 'Failed to load assigned trials');
      }
    } catch (error) {
      console.warn('API failed, falling back to localStorage:', error.message);
      // Fallback to localStorage
    try {
      const rawT = window.localStorage.getItem('trials');
      const rawA = window.localStorage.getItem('trialAssignments');
      const trials = rawT ? JSON.parse(rawT) : [];
      const assigns = (rawA ? JSON.parse(rawA) : []).filter(a => (a.assignedOfficer || '').toLowerCase() === (user?.name || '').toLowerCase());
      const byTrial = assigns.reduce((acc, a) => {
        const t = trials.find(tt => tt.id === a.trialId) || {};
        if (!acc[a.trialId]) {
          acc[a.trialId] = {
            id: a.trialId,
            trialCode: (a.locationCode || '').slice(0, -3),
            trialName: t.name || '-',
            crop: t.crop || '-',
            season: t.season || '-',
            year: t.year || '-',
            status: t.status || 'active',
            locations: []
          };
        }
        acc[a.trialId].locations.push({
          id: a.id,
          locationCode: a.locationCode,
          locationName: a.locationName,
          culture: a.culture,
          treatment: a.treatment,
          replications: a.replications,
          observations: a.observations || [],
          lastEntryDate: a.lastEntryDate || '-',
          startDate: a.assignedDate || '-',
          status: a.status || 'active'
        });
        return acc;
      }, {});
        setAssignedTrials(Object.values(byTrial));
      } catch {
        setAssignedTrials([]);
      }
    }
  };

  // Load dashboard data from API with fallback to mock data
  const loadDashboardData = async () => {
    try {
      const [summaryResponse, activitiesResponse] = await Promise.all([
        plotOfficerService.getDashboardSummary(),
        plotOfficerService.getRecentActivities()
      ]);
      
      if (summaryResponse.success) {
        setDashboardStats(summaryResponse.data || summaryResponse);
      }
      
      if (activitiesResponse.success) {
        setRecentActivities(activitiesResponse.data || activitiesResponse);
      }
    } catch (error) {
      console.warn('Failed to load dashboard data from API, using mock data:', error.message);
      // Keep existing mock data if API fails
    }
  };

  useEffect(() => {
    loadAssignedTrials();
    loadDashboardData();
  }, [user]);

  const [selectedTrial, setSelectedTrial] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showDataEntryModal, setShowDataEntryModal] = useState(false);
  const [showLocationDetailsModal, setShowLocationDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm();

  // Mock data for dropdowns
  const cultures = [
    'Hybrid Rice Variety',
    'High-Yielding Maize Hybrid',
    'Durum Wheat Variety',
    'Improved Sorghum Variety',
    'Hybrid Cotton Variety'
  ];

  const observations = [
    'Germination %',
    'Germination Vigour',
    'Population',
    'Vigour',
    'Germination DAS',
    'Plant height',
    'Plant width',
    'No. of branches',
    'Yield'
  ];

  // Fallback mock data for recent activities
  const fallbackActivities = [
    {
      id: 1,
      type: 'data-entry',
      action: 'Data entry completed',
      description: 'Germination % recorded for Coimbatore Rice Trial',
      time: '2 hours ago',
      status: 'completed',
      color: 'green'
    },
    {
      id: 2,
      type: 'trial',
      action: 'New trial assigned',
      description: 'Maize trial assigned to Salem Research Centre',
      time: '1 day ago',
      status: 'active',
      color: 'blue'
    },
    {
      id: 3,
      type: 'observation',
      action: 'Field observation scheduled',
      description: 'Plant height measurement due tomorrow',
      time: '2 days ago',
      status: 'active',
      color: 'orange'
    },
    {
      id: 4,
      type: 'location',
      action: 'Location visit completed',
      description: 'Site inspection at Madurai Research Station',
      time: '3 days ago',
      status: 'completed',
      color: 'purple'
    }
  ];

  const displayActivities = recentActivities.length > 0 ? recentActivities : fallbackActivities;

  const [activeTabTrialId, setActiveTabTrialId] = useState(null);

  const filteredTrials = assignedTrials.filter(trial => {
    const matchesSearch = trial.trialName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trial.trialCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trial.crop.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || trial.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleDataEntry = async (data) => {
    try {
      setIsLoading(true);
      
      const payload = {
        trialId: selectedTrial.id,
        locationId: selectedLocation.id,
        entryDate: data.entryDate,
        culture: data.culture,
        observation: data.observation,
        replicationData: data.replicationData
      };

      const response = await plotOfficerService.submitDataEntry(payload);
      if (response.success) {
      toast.success('Data entry saved successfully!');
      setShowDataEntryModal(false);
      reset();
      setSelectedLocation(null);
        // Reload data to reflect changes
        await loadAssignedTrials();
      } else {
        throw new Error(response.message || 'Failed to save data entry');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to save data entry');
    } finally {
      setIsLoading(false);
    }
  };

  const openDataEntryModal = (trial, location) => {
    setSelectedTrial(trial);
    setSelectedLocation(location);
    setValue('entryDate', new Date().toISOString().split('T')[0]);
    setValue('culture', location.culture);
    setShowDataEntryModal(true);
  };

  const openLocationDetailsModal = (location) => {
    setSelectedLocation(location);
    setShowLocationDetailsModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeasonColor = (season) => {
    switch (season) {
      case 'Kharif':
        return 'bg-green-100 text-green-600';
      case 'Rabi':
        return 'bg-blue-100 text-blue-600';
      case 'Winter':
        return 'bg-gray-100 text-gray-600';
      case 'Summer':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'data-entry':
        return <BarChart3 className="w-5 h-5" />;
      case 'trial':
        return <FileText className="w-5 h-5" />;
      case 'observation':
        return <Eye className="w-5 h-5" />;
      case 'location':
        return <MapPin className="w-5 h-5" />;
      default:
        return <Activity className="w-5 h-5" />;
    }
  };

  const getActivityColor = (color) => {
    switch (color) {
      case 'green':
        return 'text-green-600';
      case 'blue':
        return 'text-blue-600';
      case 'orange':
        return 'text-orange-600';
      case 'purple':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Welcome back, Plot Officer!</h1>
        <p className="text-primary-100">
          TNAU Plot Officer Dashboard - Manage your assigned trials and data entry tasks.
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Trials */}
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Trials</p>
              <p className="text-3xl font-bold text-gray-900">{dashboardStats.trials.total}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Active Trials</span>
              <span className="text-green-600 font-medium">{dashboardStats.trials.active}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Completed</span>
              <span className="text-blue-600 font-medium">{dashboardStats.trials.completed}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Pending</span>
              <span className="text-orange-600 font-medium">{dashboardStats.trials.pending}</span>
            </div>
          </div>
        </div>

        {/* Total Locations */}
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Locations</p>
              <p className="text-3xl font-bold text-gray-900">{dashboardStats.locations.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Active</span>
              <span className="text-green-600 font-medium">{dashboardStats.locations.active}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Pending</span>
              <span className="text-orange-600 font-medium">{dashboardStats.locations.pending}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Completed</span>
              <span className="text-blue-600 font-medium">{dashboardStats.locations.completed}</span>
            </div>
          </div>
        </div>

        {/* Data Entries */}
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Data Entries</p>
              <p className="text-3xl font-bold text-gray-900">{dashboardStats.dataEntries.total}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">This Week</span>
              <span className="text-green-600 font-medium">{dashboardStats.dataEntries.thisWeek}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Pending</span>
              <span className="text-orange-600 font-medium">{dashboardStats.dataEntries.pending}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Overdue</span>
              <span className="text-red-600 font-medium">{dashboardStats.dataEntries.overdue}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geographic Overview */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Geographic Overview</h2>
          <div className="bg-gray-100 rounded-lg p-8 text-center">
            <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Interactive Map</p>
            <p className="text-sm text-gray-500 mt-2">Showing your assigned trial locations</p>
            <div className="flex justify-center space-x-4 mt-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-xs text-gray-600">Active Trials</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                <span className="text-xs text-gray-600">Locations</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                <span className="text-xs text-gray-600">Data Points</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h2>
          <div className="space-y-4">
                              {displayActivities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getActivityColor(activity.color)} bg-gray-100`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-sm text-gray-600">{activity.description}</p>
                  <div className="flex items-center mt-1">
                    <Clock className="w-3 h-3 text-gray-400 mr-1" />
                    <span className="text-xs text-gray-500">{activity.time}</span>
                    <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(activity.status)}`}>
                      {activity.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <button className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <Plus className="w-8 h-8 text-green-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">New Entry</span>
          </button>
          <button className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <Eye className="w-8 h-8 text-blue-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">View Trials</span>
          </button>
          <button className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <MapPin className="w-8 h-8 text-purple-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">Locations</span>
          </button>
          <button className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <BarChart3 className="w-8 h-8 text-orange-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">Reports</span>
          </button>
          <button className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <Calendar className="w-8 h-8 text-red-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">Schedule</span>
          </button>
          <button className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <Play className="w-8 h-8 text-indigo-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">Start Work</span>
          </button>
        </div>
      </div>

      

      {/* Data Entry Modal */}
      {showDataEntryModal && selectedTrial && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Data Entry: {selectedLocation.locationName}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Trial: {selectedTrial.trialName} ({selectedTrial.trialCode})
              </p>
              
              <form onSubmit={handleSubmit(handleDataEntry)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entry Date</label>
                  <input
                    type="date"
                    {...register('entryDate', { required: 'Entry date is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    readOnly
                  />
                  {errors.entryDate && <p className="text-red-500 text-xs mt-1">{errors.entryDate.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Culture</label>
                  <select
                    {...register('culture', { required: 'Culture is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select Culture</option>
                    {cultures.map(culture => (
                      <option key={culture} value={culture}>{culture}</option>
                    ))}
                  </select>
                  {errors.culture && <p className="text-red-500 text-xs mt-1">{errors.culture.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observation</label>
                  <select
                    {...register('observation', { required: 'Observation is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select Observation</option>
                    {selectedLocation.observations.map(obs => (
                      <option key={obs} value={obs}>{obs}</option>
                    ))}
                  </select>
                  {errors.observation && <p className="text-red-500 text-xs mt-1">{errors.observation.message}</p>}
                </div>

                {/* Replication Data Entry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Replication Data (Based on {selectedLocation.replications} replications)
                  </label>
                  <div className="space-y-4">
                    {Array.from({ length: selectedLocation.replications }, (_, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Replication {index + 1} (R{index + 1})</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Sub-rep V1</label>
                            <input
                              type="number"
                              step="0.01"
                              {...register(`replicationData.${index}.v1`, { 
                                required: `V1 value for R${index + 1} is required`,
                                min: { value: 0, message: 'Value must be positive' }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                              placeholder="Enter V1 value"
                            />
                            {errors.replicationData?.[index]?.v1 && (
                              <p className="text-red-500 text-xs mt-1">{errors.replicationData[index].v1.message}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Sub-rep V2</label>
                            <input
                              type="number"
                              step="0.01"
                              {...register(`replicationData.${index}.v2`, { 
                                required: `V2 value for R${index + 1} is required`,
                                min: { value: 0, message: 'Value must be positive' }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                              placeholder="Enter V2 value"
                            />
                            {errors.replicationData?.[index]?.v2 && (
                              <p className="text-red-500 text-xs mt-1">{errors.replicationData[index].v2.message}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Sub-rep V3</label>
                            <input
                              type="number"
                              step="0.01"
                              {...register(`replicationData.${index}.v3`, { 
                                required: `V3 value for R${index + 1} is required`,
                                min: { value: 0, message: 'Value must be positive' }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                              placeholder="Enter V3 value"
                            />
                            {errors.replicationData?.[index]?.v3 && (
                              <p className="text-red-500 text-xs mt-1">{errors.replicationData[index].v3.message}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDataEntryModal(false);
                      reset();
                      setSelectedLocation(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? 'Saving...' : 'Save Data Entry'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Location Details Modal */}
      {showLocationDetailsModal && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Location Details: {selectedLocation.locationName}
              </h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Location Code</p>
                    <p className="text-sm text-gray-900 font-mono">{selectedLocation.locationCode}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Culture & Treatment</p>
                    <p className="text-sm text-gray-900">{selectedLocation.culture} - {selectedLocation.treatment}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Number of Replications</p>
                    <p className="text-sm text-gray-900">{selectedLocation.replications}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Last Entry Date</p>
                    <p className="text-sm text-gray-900">{selectedLocation.lastEntryDate}</p>
                  </div>

                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Observations to Record</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedLocation.observations.map((obs, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {obs}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowLocationDetailsModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlotOfficerDashboard; 